import { useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useShallow } from "zustand/shallow";

import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useTrackPageView } from "hooks/useTrackPageView";
import {
  useSitesKpisProgressQuery,
  useSitesProgressQuery
} from "hooks/queries/kpis";
import { Site } from "interfaces/kpis";
import { useKpisStore } from "stores/kpis.store";
import { KpisList } from "pages/Kpis/KpisList";
import { KpisHeader } from "pages/Kpis/KpisHeader";
import { KpisLinearProgress } from "pages/Kpis/KpisLinearProgress";
import { TurbineList } from "pages/Kpis/TurbineList";
import { LinkButton } from "components/LinkButton";
import { getGroupedByActivityType } from "pages/Kpis/utils/getGroupedByActivityType";

const PAGE_TITLE = "KPIs";

export const KpisPage = (): JSX.Element => {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);

  const { setSelectedSite, selectedSite } = useKpisStore(
    useShallow(store => ({
      selectedSite: store.selectedSite,
      setSelectedSite: store.setSelectedSite
    }))
  );

  const { selectedYear } = useKpisStore(
    useShallow(store => ({
      selectedYear: store.selectedYear,
      setSelectedYear: store.setSelectedYear
    }))
  );

  const {
    data: annualKpis,
    isLoading: isKpisLoading,
    isError: isKpisError
  } = useSitesKpisProgressQuery({
    page: 1,
    size: 999_999,
    site_id: selectedSite?.id,
    year: selectedYear,
    enabled: !!selectedYear
  });

  const {
    data: kpisSites,
    isLoading: isKpisSitesLoading,
    isError: isKpisSitesError
  } = useSitesProgressQuery({
    page: 1,
    size: 999_999,
    year: selectedYear
  });

  useEffect(() => {
    if (!selectedSite && kpisSites) {
      setSelectedSite(kpisSites.results[0]);
    }
  }, [kpisSites, selectedSite, setSelectedSite]);

  const { inspection, maintenance } = useMemo(() => {
    if (!isKpisLoading && !isKpisError) {
      return getGroupedByActivityType(annualKpis?.results);
    }
    return { targeted: [], others: [] };
  }, [isKpisLoading, isKpisError, annualKpis?.results]);

  const handleLoadMoreProjects = (): void => {};

  return (
    <>
      <CssGrid
        sx={{
          minHeight: "calc(100vh - 100px)",
          maxHeight: "calc(100vh - 100px)"
        }}
      >
        <PaddedPaper sx={{ gridArea: "s1" }}>
          <TitleAndButtonWrapper>
            <Typography variant="h4">KPIs</Typography>

            <LinkButton to="/pages/kpis/manage" color="primary">
              <Stack direction="row" alignItems="center" gap={1}>
                <SettingsIcon /> Manage KPIs
              </Stack>
            </LinkButton>
          </TitleAndButtonWrapper>

          <KpisList
            loadingInitialProjects={isKpisSitesLoading}
            // loadingMoreProjects={fetchProjects.isFetchingNextPage}
            error={isKpisSitesError}
            items={kpisSites?.results ?? ([] as Site[])}
            onScrolledToEnd={handleLoadMoreProjects}
          />
        </PaddedPaper>

        {selectedSite ? (
          <>
            <PaddedPaper sx={{ gridArea: "s3", minWidth: 0 }}>
              <Stack spacing={0} minHeight={0}>
                <KpisHeader />

                <Typography variant="h5" component="h2" mt={1}>
                  KPIs
                </Typography>

                <Stack direction="column" minHeight={0} overflow="auto">
                  {[inspection, maintenance].map(items => (
                    <>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ textTransform: "capitalize", mt: 2.5, mb: 1 }}
                      >
                        {items?.[0]?.activity_type}
                      </Typography>

                      {items?.map(item => {
                        const siteId = `site-${item.id}`;
                        let kpiTitle = item.name;
                        if (item.workplan_name) {
                          kpiTitle += ` (${item.workplan_name})`;
                        }

                        return (
                          <Accordion
                            key={item.id}
                            data-annual-kpi-id={item.id}
                            disableGutters
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls={siteId}
                              sx={{
                                ".MuiAccordionSummary-content": {
                                  minWidth: 0,
                                  container:
                                    "accordion-summary-content / inline-size"
                                }
                              }}
                            >
                              <AccordionSummaryContent>
                                <Tooltip
                                  title={kpiTitle}
                                  placement="bottom-start"
                                  className="kpi-title-tooltip"
                                >
                                  <Typography
                                    noWrap
                                    width="100%"
                                    className="kpi-title"
                                  >
                                    {kpiTitle}
                                  </Typography>
                                </Tooltip>

                                <Tooltip
                                  title={item.description}
                                  placement="bottom-start"
                                  className="kpi-description-tooltip"
                                >
                                  <Typography
                                    noWrap
                                    width="100%"
                                    className="kpi-description"
                                    color="text.secondary"
                                    variant="body2"
                                  >
                                    {item.description}
                                  </Typography>
                                </Tooltip>

                                <Stack
                                  direction="row"
                                  gap={2}
                                  justifyContent="space-between"
                                  width="min-content"
                                  className="kpi-progress"
                                >
                                  <Chip
                                    label={item.category_name ?? "None"}
                                    size="small"
                                  />
                                  <KpisLinearProgress
                                    variant="determinate"
                                    value={item.progress.percentage_metric}
                                    sx={{
                                      flexGrow: 0,
                                      flexShrink: 0,
                                      width: "300px"
                                    }}
                                  />
                                </Stack>
                              </AccordionSummaryContent>
                            </AccordionSummary>
                            <AccordionDetails id={siteId}>
                              <TurbineList
                                kpiId={item.id}
                                siteId={selectedSite.id}
                              />
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </>
                  ))}
                </Stack>
                {/* END */}
              </Stack>
            </PaddedPaper>
          </>
        ) : (
          <NoDataTextContainer>Nothing to show</NoDataTextContainer>
        )}
      </CssGrid>
    </>
  );
};

const TitleAndButtonWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
TitleAndButtonWrapper.displayName = "TitleAndButtonWrapper";

const NoDataTextContainer = styled("div")`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  font-size: 24px;
  font-weight: 600;
`;
NoDataTextContainer.displayName = "NoDataTextContainer";

const PaddedPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: ${({ theme }) => theme.spacing(2)};
`;

PaddedPaper.displayName = "PaddedPaper";

const CssGrid = styled(Box)`
  box-sizing: border-box;
  height: 100%;
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};

  ${props => props.theme.breakpoints.down("sm")} {
    grid-template-columns: auto;
    grid-template-rows: auto;
    grid-template-areas:
      "s1"
      "s3";
  }
  ${props => props.theme.breakpoints.up("sm")} {
    grid-template-columns: 280px 1fr;
    grid-template-rows: 1fr;
    grid-template-areas: "s1 s3";
  }
  ${props => props.theme.breakpoints.up("md")} {
    grid-template-columns: 380px 1fr;
    grid-template-rows: 1fr;
    grid-template-areas: "s1 s3";
  }
`;
CssGrid.displayName = "CssGrid";

const AccordionSummaryContent = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: left;
  justify-content: space-between;
  column-gap: ${({ theme }) => theme.spacing(3)};
  row-gap: ${({ theme }) => theme.spacing(1)};
  flex-wrap: wrap;
  width: 100%;

  .kpi-title-tooltip {
    order: 1;
    flex: 1 1 0;
    min-width: 0;
    max-width: 100%;
    /* Ensures it can shrink and grow as needed */
    display: flex;
    align-items: center;
  }

  .kpi-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    display: block;
  }

  .kpi-description {
    order: 3;
  }

  .kpi-progress {
    order: 2;
    flex-shrink: 0;
    width: min-content;
    min-width: 0;
    max-width: none;
  }

  @container accordion-summary-content (width < 80ch) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing(1)};

    .kpi-title-tooltip {
      order: 1;
      width: 100%;
      max-width: 100%;
      flex: none; // Prevent shrinking vertically
    }

    .kpi-description {
      order: 2;
    }

    .kpi-progress {
      order: 3;
    }
  }
`;
AccordionSummaryContent.displayName = "AccordionSummaryContent";
